/** Parse textual decimal values without ever passing through floating point. */
export function parseDecimal(value: string, places: number): bigint {
  if (typeof value !== 'string' || !/^-?\d+(\.\d+)?$/.test(value)) {
    throw new Error(`Valor decimal inválido: ${String(value)}`);
  }
  const negative = value.startsWith('-');
  const [whole, fraction = ''] = (negative ? value.slice(1) : value).split('.');
  if (fraction.length > places) throw new Error(`Valor ${value} excede a escala de ${places} casas.`);
  const scaled = BigInt(whole + fraction.padEnd(places, '0'));
  return negative ? -scaled : scaled;
}
export function serializeDecimal(value: bigint, places: number): string {
  const negative = value < BigInt(0);
  const digits = (negative ? -value : value).toString().padStart(places + 1, '0');
  return `${negative ? '-' : ''}${places ? `${digits.slice(0, -places)}.${digits.slice(-places)}` : digits}`;
}
// Bound the process-wide formatter cache; per-dataset value caches live in presentation.ts.
const formatters = new Map<string, (value: string) => string>();
function createFormatter(places: number, locale: string, currency?: string): (value: string) => string {
  const formatter = new Intl.NumberFormat(locale, {
    style: currency ? 'currency' : 'decimal', ...(currency ? { currency } : {}),
    minimumFractionDigits: places, maximumFractionDigits: places,
  });
  const digitFormatter = new Intl.NumberFormat(locale, { useGrouping: false });
  const digits = Array.from({ length: 10 }, (_, n) => digitFormatter.format(n));
  const numericTypes = new Set(['integer', 'group', 'decimal', 'fraction']);
  const affixes = [1, -1].map(sign => {
    const parts = formatter.formatToParts(sign);
    const first = parts.findIndex(p => p.type === 'integer');
    let last = first;
    parts.forEach((p, i) => { if (numericTypes.has(p.type)) last = i; });
    return [parts.slice(0, first).map(p => p.value).join(''), parts.slice(last + 1).map(p => p.value).join('')];
  });
  const scale = BigInt(10) ** BigInt(places);
  return value => {
    const scaled = parseDecimal(value, places);
    const negative = scaled < BigInt(0);
    const absolute = negative ? -scaled : scaled;
    const fraction = (absolute % scale).toString().padStart(places, '0').split('').map(d => digits[Number(d)]).join('');
    const number = formatter.formatToParts(absolute / scale).filter(p => numericTypes.has(p.type))
      .map(p => p.type === 'fraction' ? fraction : p.value).join('');
    const [prefix, suffix] = affixes[negative ? 1 : 0];
    return prefix + number + suffix;
  };
}
/** Reuse Intl instances and sign/digit patterns; BigInt precision remains intact. */
export function formatDecimal(value: string, places: number, locale: string, currency?: string): string {
  const key = JSON.stringify([places, locale, currency]);
  let format = formatters.get(key);
  if (!format) {
    format = createFormatter(places, locale, currency);
    if (formatters.size >= 32) formatters.delete(formatters.keys().next().value!);
    formatters.set(key, format);
  }
  return format(value);
}
